/**
 * The grid of a border on an elevation sheet (ADR-0020, tiers B and C): its rows between
 * floor lines, where a new line snaps, which line the pointer is on, and the operations
 * that repeat or move one. The server works out the cells and units; this is only what
 * the keys need to say which line and where.
 */
import type { WorkspaceConfig, WorkspaceOp } from "@/api/client";

/** A chosen line: a floor line (`break`, at a y) or a bay line (`vline`, at an x). */
export type GridLine = { kind: "break" | "vline"; at: number };

const SAME = 1e-6;
const GAP_MIN = -0.0005;
const GAP_MAX = 0.004;
const FLOOR_REACH = 0.012;
const BAY_REACH = 0.008;

/** The border's rows, top down, between its floor lines. */
export function rowsOf(c: WorkspaceConfig): [number, number][] {
  const { y0, y1 } = c.bbox_norm;
  const ys = [y0, ...c.breaks.filter((y) => y > y0 && y < y1).sort((a, b) => a - b), y1];
  return ys.slice(0, -1).map((y, i) => [y, ys[i + 1]!]);
}

/** The row holding `y`, or -1. */
export function rowAt(c: WorkspaceConfig, y: number): number {
  return rowsOf(c).findIndex(([a, b]) => a <= y && y <= b);
}

function nearest(v: number, candidates: readonly number[], reach: number): number {
  let best = v;
  let d = reach;
  for (const c of candidates) {
    if (Math.abs(c - v) < d) {
      d = Math.abs(c - v);
      best = c;
    }
  }
  return best;
}

/** A floor line snaps to the nearest gap between two panes one over the other. */
export function snapY(c: WorkspaceConfig, y: number): number {
  const gaps: number[] = [];
  for (const a of c.panes) {
    for (const b of c.panes) {
      const gap = b.bbox_norm.y0 - a.bbox_norm.y1;
      const over =
        Math.min(a.bbox_norm.x1, b.bbox_norm.x1) - Math.max(a.bbox_norm.x0, b.bbox_norm.x0);
      if (gap >= GAP_MIN && gap < GAP_MAX && over > 0)
        gaps.push((a.bbox_norm.y1 + b.bbox_norm.y0) / 2);
    }
  }
  return nearest(y, gaps, FLOOR_REACH);
}

/** A bay line snaps to the nearest mullion: the gap between two panes side by side. */
export function snapX(c: WorkspaceConfig, x: number): number {
  const gaps: number[] = [];
  for (const a of c.panes) {
    for (const b of c.panes) {
      const gap = b.bbox_norm.x0 - a.bbox_norm.x1;
      const over =
        Math.min(a.bbox_norm.y1, b.bbox_norm.y1) - Math.max(a.bbox_norm.y0, b.bbox_norm.y0);
      if (gap >= GAP_MIN && gap < GAP_MAX && over > 0)
        gaps.push((a.bbox_norm.x1 + b.bbox_norm.x0) / 2);
    }
  }
  return nearest(x, gaps, BAY_REACH);
}

/** The line the pointer is on, within `reach`; the nearer of a floor and a bay line. */
export function lineNear(
  c: WorkspaceConfig,
  [x, y]: [number, number],
  reach: number,
): GridLine | null {
  const b = c.bbox_norm;
  if (x < b.x0 - reach || x > b.x1 + reach || y < b.y0 - reach || y > b.y1 + reach) return null;
  let best: GridLine | null = null;
  let d = reach;
  for (const at of c.breaks) {
    if (Math.abs(at - y) < d) {
      d = Math.abs(at - y);
      best = { kind: "break", at };
    }
  }
  for (const v of c.vlines) {
    const crosses = v.y0 === null || v.y1 === null || (v.y0 <= y && y <= v.y1);
    if (crosses && Math.abs(v.x - x) < d) {
      d = Math.abs(v.x - x);
      best = { kind: "vline", at: v.x };
    }
  }
  return best;
}

/** A line is still there (after an edit, or an undo that took it away). */
export function hasLine(c: WorkspaceConfig, line: GridLine): boolean {
  return line.kind === "break"
    ? c.breaks.some((y) => Math.abs(y - line.at) < SAME)
    : c.vlines.some((v) => Math.abs(v.x - line.at) < SAME);
}

/**
 * Lines that repeat `line` at its spacing to the nearest line or edge beside it, away
 * from that one, to the border's other edge; each snapped.
 */
export function repeatOps(c: WorkspaceConfig, line: GridLine): WorkspaceOp[] {
  const b = c.bbox_norm;
  const id = c.id;
  if (line.kind === "break") {
    const all = [b.y0, ...c.breaks, b.y1].sort((p, q) => p - q);
    const above = all.filter((v) => v < line.at - SAME).pop() ?? b.y0;
    const below = all.find((v) => v > line.at + SAME) ?? b.y1;
    const down = line.at - above <= below - line.at;
    const step = down ? line.at - above : below - line.at;
    if (step <= SAME) return [];
    const out: WorkspaceOp[] = [];
    for (
      let v = line.at + (down ? step : -step);
      down ? v < b.y1 - step * 0.3 : v > b.y0 + step * 0.3;
      v += down ? step : -step
    ) {
      out.push({ op: "add_break", configuration_id: id, y: snapY(c, v) });
    }
    return out;
  }
  const v = c.vlines.find((o) => Math.abs(o.x - line.at) < SAME);
  if (!v) return [];
  const all = [b.x0, ...c.vlines.filter((o) => o.y0 === v.y0).map((o) => o.x), b.x1].sort(
    (p, q) => p - q,
  );
  const left = all.filter((o) => o < line.at - SAME).pop() ?? b.x0;
  const right = all.find((o) => o > line.at + SAME) ?? b.x1;
  const rightward = line.at - left <= right - line.at;
  const step = rightward ? line.at - left : right - line.at;
  if (step <= SAME) return [];
  const out: WorkspaceOp[] = [];
  for (
    let u = line.at + (rightward ? step : -step);
    rightward ? u < b.x1 - step * 0.3 : u > b.x0 + step * 0.3;
    u += rightward ? step : -step
  ) {
    out.push({ op: "add_vline", configuration_id: id, x: snapX(c, u), y0: v.y0, y1: v.y1 });
  }
  return out;
}

/** The operations that move `line` by `d` (in its own direction), and where it lands. */
export function nudgeOps(
  c: WorkspaceConfig,
  line: GridLine,
  d: number,
): { ops: WorkspaceOp[]; to: GridLine } | null {
  const to = Math.min(1, Math.max(0, line.at + d));
  if (line.kind === "break") {
    return {
      ops: [
        { op: "remove_break", configuration_id: c.id, y: line.at },
        { op: "add_break", configuration_id: c.id, y: to },
      ],
      to: { kind: "break", at: to },
    };
  }
  const v = c.vlines.find((o) => Math.abs(o.x - line.at) < SAME);
  if (!v) return null;
  return {
    ops: [
      { op: "remove_vline", configuration_id: c.id, x: line.at },
      { op: "add_vline", configuration_id: c.id, x: to, y0: v.y0, y1: v.y1 },
    ],
    to: { kind: "vline", at: to },
  };
}

/** The operation that takes `line` away. */
export function removeOp(c: WorkspaceConfig, line: GridLine): WorkspaceOp {
  return line.kind === "break"
    ? { op: "remove_break", configuration_id: c.id, y: line.at }
    : { op: "remove_vline", configuration_id: c.id, x: line.at };
}

/** A row's typical count: how many floors it stands for. */
export function typicalOf(c: WorkspaceConfig, row: number): number {
  return c.typical.find((t) => t.row === row)?.count ?? 1;
}
